(() => {
  if (window.QLSEnhancedMobileMenuV124Loaded) return;
  window.QLSEnhancedMobileMenuV124Loaded = true;

  const mobileQuery = window.matchMedia('(max-width: 768px)');

  function getMenu(element) {
    return element?.closest?.('main-menu') || document.querySelector('main-menu');
  }

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  async function refreshCartSummary() {
    const summaries = document.querySelectorAll('[data-qls-mobile-cart-summary]');
    if (!summaries.length) return;

    try {
      const response = await fetch(`${theme.routes.cart}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) return;
      const cart = await response.json();
      summaries.forEach((summary) => {
        const count = summary.querySelector('[data-qls-mobile-cart-count]');
        const total = summary.querySelector('[data-qls-mobile-cart-total]');
        if (count) count.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        if (total) total.textContent = formatMoney(cart.total_price);
      });
    } catch (error) {
      // The normal cart icon remains the source of truth if this optional refresh fails.
    }
  }

  function syncOpenState(menu) {
    const toggle = menu?.querySelector('.main-menu__toggle');
    const content = menu?.querySelector('.main-menu__content');
    const open = Boolean(
      mobileQuery.matches &&
      (toggle?.getAttribute('aria-expanded') === 'true' || content?.classList.contains('is-open'))
    );
    document.documentElement.classList.toggle('qls-mobile-menu-open', open);
  }

  function closeMenu(menu, preserveExternalFocus = false) {
    if (!menu) return;

    /* The theme normally returns focus to the hamburger when closing. When the
       drawer launches search, that would immediately steal focus from the real
       search input and prevent the iOS keyboard from staying open. */
    if (preserveExternalFocus && typeof menu.closeMainMenu === 'function') {
      menu.opener = null;
      menu.closeMainMenu(false);
    } else {
      const toggle = menu.querySelector('.main-menu__toggle');
      if (toggle?.getAttribute('aria-expanded') === 'true') toggle.click();
    }

    window.requestAnimationFrame(() => syncOpenState(menu));
  }

  function measureTools(menu) {
    if (!mobileQuery.matches || !menu) return;
    const content = menu.querySelector('.main-menu__content');
    const tools = menu.querySelector('[data-qls-mobile-menu-tools]');
    if (!content || !tools) return;
    content.style.setProperty('--qls-mobile-menu-tools-height', `${Math.ceil(tools.getBoundingClientRect().height)}px`);
  }

  function observeTools(menu) {
    if (!menu || menu.dataset.qlsToolsObserved === 'true') return;
    const tools = menu.querySelector('[data-qls-mobile-menu-tools]');
    if (!tools) return;
    menu.dataset.qlsToolsObserved = 'true';

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => measureTools(menu));
      observer.observe(tools);
      menu._qlsToolsResizeObserver = observer;
    }
  }

  function focusAdvancedSearch(menu) {
    /* Focus the real header input during the original tap before removing the
       drawer focus trap. This is the reliable order for iOS to show the
       keyboard and leave the caret in the search field. */
    const header = document.querySelector('store-header');
    const input = document.getElementById('header-search') || header?.querySelector('input[type="search"]');

    window.QLSMobileSearchHandoffUntil = Date.now() + 1800;
    document.documentElement.classList.remove('qls-mobile-search-compact');
    document.documentElement.classList.add('qls-mobile-search-opened', 'qls-mobile-search-focus');
    window.QLSMobileInputFocusGuardUntil = Date.now() + 3200;

    if (typeof window.QLSOpenMobileSearch === 'function') {
      window.QLSOpenMobileSearch();
    } else if (input) {
      try { input.focus({ preventScroll: true }); } catch (error) { input.focus(); }
    }

    closeMenu(menu, true);
    document.documentElement.classList.remove('qls-mobile-menu-open', 'qls-mobile-menu-top-offset');

    /* Fallback refocuses do not create the keyboard by themselves, but they do
       protect the caret if a browser transition briefly moves focus. */
    window.requestAnimationFrame(() => {
      if (input && document.activeElement !== input) {
        try { input.focus({ preventScroll: true }); } catch (error) { input.focus(); }
      }
    });
    window.setTimeout(() => {
      if (input && document.activeElement !== input) {
        try { input.focus({ preventScroll: true }); } catch (error) { input.focus(); }
      }
    }, 80);
  }

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-qls-mobile-menu-close], [data-qls-mobile-menu-backdrop]');
    if (closeButton) {
      event.preventDefault();
      closeMenu(getMenu(closeButton));
      return;
    }

    const searchLauncher = event.target.closest('[data-qls-mobile-search-launcher]');
    if (searchLauncher) {
      event.preventDefault();
      focusAdvancedSearch(getMenu(searchLauncher));
      return;
    }

    const menuToggle = event.target.closest('.main-menu__toggle');
    if (menuToggle) {
      if (typeof window.QLSCloseHeaderDeliveryMenus === 'function') {
        window.QLSCloseHeaderDeliveryMenus();
      }
      const menu = getMenu(menuToggle);
      window.requestAnimationFrame(() => {
        syncOpenState(menu);
        observeTools(menu);
        measureTools(menu);
        if (menuToggle.getAttribute('aria-expanded') === 'true') refreshCartSummary();
      });
    }
  });

  document.addEventListener('qls:cart-item-touched', refreshCartSummary);
  document.addEventListener('qls:cart-quantity-committed', refreshCartSummary);

  document.addEventListener('transitionend', (event) => {
    if (event.target.matches?.('.main-menu__content')) {
      const menu = getMenu(event.target);
      syncOpenState(menu);
      measureTools(menu);
    }
  });

  window.addEventListener('resize', () => {
    if (!mobileQuery.matches) {
      document.documentElement.classList.remove('qls-mobile-menu-open');
      return;
    }
    document.querySelectorAll('main-menu').forEach((menu) => {
      syncOpenState(menu);
      measureTools(menu);
    });
  }, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    const menu = event.target.querySelector?.('main-menu');
    if (menu) { observeTools(menu); measureTools(menu); }
  });

  document.querySelectorAll('main-menu').forEach((menu) => {
    observeTools(menu);
    measureTools(menu);
  });
})();
